import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import { type DayOfWeek } from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

const DEFAULT_LOCALE =
    typeof navigator !== 'undefined' ? navigator.language : 'en-US';

export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** JS `Date.getDay()` index for each `DayOfWeek` (Sunday = 0). */
export const WEEKDAY_INDEX_BY_DAY: Record<DayOfWeek, WeekdayIndex> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

/**
Formats date and time in timezone
@example "Jan 15, 2025 at 2:30 PM"
*/
export function formatDateAndTime(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(dt);
}

/**
  Formats time only in timezone
  @example "2:30 PM"
  */
export function formatTime(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        timeStyle: 'short',
    }).format(dt);
}

// Cached per timezone: calendar grids format hundreds of chip times per
// render and Intl.DateTimeFormat construction is comparatively expensive.
const COMPACT_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/**
  Compact time for dense calendar surfaces, dropping :00 minutes
  @example "2p", "4:30p"
  */
export function formatCompactTime(iso: string, tz: string): string {
    let formatter = COMPACT_TIME_FORMATTERS.get(tz);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        COMPACT_TIME_FORMATTERS.set(tz, formatter);
    }
    const parts = formatter.formatToParts(new Date(iso));
    const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
    const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
    const period =
        parts
            .find((part) => part.type === 'dayPeriod')
            ?.value.toLocaleLowerCase()
            .slice(0, 1) ?? '';

    return minute === '00' ? `${hour}${period}` : `${hour}:${minute}${period}`;
}

/**
  Formats time in 24-hour format (HH:MM) in timezone
  @example "14:30"
  */
export function formatTime24Hour(iso: string, tz: string): string {
    const dt = new Date(iso);
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
    });
    return formatter.format(dt);
}

/**
  Formats date with year in timezone
  @example "Jan 15, 2025"
  */
export function formatFullDate(
    iso: string | Date,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(dt);
}

/**
  Formats day of week in timezone
  @example "Monday"
  */
export function formatDayOfWeek(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        weekday: 'long',
    }).format(dt);
}

/**
  Formats abbreviated day of week in timezone
  @example "Mon"
  */
export function formatShortDayOfWeek(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        weekday: 'short',
    }).format(dt);
}

/**
 * Compact inbox timestamp in the org timezone.
 * Same calendar day: time ("3:50 PM").
 * Same Sunday–Saturday week, not today: weekday ("Wed").
 * Older: numeric date ("4/25/26").
 */
export function formatInboxTimestamp(
    iso: string,
    tz: string,
    now: Date = new Date(),
    locale = DEFAULT_LOCALE,
): string {
    const activityKey = toDateKey(new Date(iso), tz);
    const nowKey = toDateKey(now, tz);
    if (activityKey === nowKey) {
        return formatTime(iso, tz, locale);
    }
    if (startOfWeekDateKey(activityKey) === startOfWeekDateKey(nowKey)) {
        return formatShortDayOfWeek(iso, tz, locale);
    }
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
    }).format(new Date(iso));
}

/**
  Formats the year in timezone
  @example "2025"
  */
export function formatYear(
    iso: string | Date,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        year: 'numeric',
    }).format(dt);
}

/**
  Formats date without year in timezone
  @example "Jan 15"
  */
export function formatDate(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        month: 'short',
        day: 'numeric',
    }).format(dt);
}

/**
  Formats date without year, with abbreviated weekday, in timezone
  @example "Sun, Jan 15"
  */
export function formatDateWithWeekday(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(dt);
}

export function formatWeekdayDateAndTime(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).format(new Date(iso));
}

/**
  Formats date and time with abbreviated weekday in timezone
  @example "Sat, Jan 15, 2025, 2:30 PM"
  */
export function formatDateAndTimeWithWeekday(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).format(dt);
}

/**
  Formats date with weekday and year in timezone
  @example "Wed, Jan 15, 2025"
  */
export function formatLongDate(
    iso: string | Date,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(dt);
}

/**
  Returns the numeric offset from UTC, in minutes, for a specific instant
  in a given IANA time-zone.
  Offset semantics
  ────────────────
  •  West of Greenwich → negative numbers
  •  UTC-05:00 → -300
  •  East of Greenwich → positive numbers
  •  UTC+09:30 → +570
  Browser support
  ───────────────
  Relies on Intl.DateTimeFormat with timeZoneName: 'shortOffset', shipped in
  Chromium 124+, Firefox 130+, and Safari TP — essentially all evergreen
  browsers released after Q2 2024.
  If you must support older engines, polyfill or fall back to date-fns-tz /
  Luxon.
  @param date  Date - the exact instant you want to ask about.
  Must be a valid JavaScript `Date` (not `"YYYY-MM-DD"`).
  @param tz    string - canonical IANA zone ID
  (`'America/Chicago'`, `'Europe/Paris'`, …).
  @returns     number - minutes that must be added to UTC to reach local
  wall-clock time at that instant.
  @example
  // Standard time (EST)
  offsetMinutes(new Date('2025-01-15T12:00:00Z'), 'America/New_York');
  // → -300
  // Daylight time (EDT)
  offsetMinutes(new Date('2025-07-15T12:00:00Z'), 'America/New_York');
  // → -240
  */
export function offsetMinutes(date: Date, tz: string): number {
    // Use 'en-US' locale for consistent parsing of the offset string structure
    // The actual offset value is independent of the locale.
    const parts = Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'shortOffset', // e.g., GMT-5, GMT+5:30, UTC, -04:00
    }).formatToParts(date);

    const timeZoneNamePart = parts.find((p) => p.type === 'timeZoneName');

    if (!timeZoneNamePart || !timeZoneNamePart.value) {
        // Fallback for timezones like 'UTC', 'GMT' if Intl doesn't provide a numeric offset part
        // or if the part is missing for some reason.
        const upperTz = tz.toUpperCase();
        if (
            upperTz === 'UTC' ||
            upperTz === 'GMT' ||
            upperTz === 'ETC/UTC' ||
            upperTz === 'ETC/GMT'
        ) {
            return 0;
        }
        // If it's an unknown timezone or unexpected Intl behavior, warn and return 0.
        console.error(
            `offsetMinutes: Could not find timeZoneName part for timezone '${tz}'. Assuming 0 offset.`,
        );
        return 0;
    }

    let offsetString = timeZoneNamePart.value; // e.g., "GMT-4", "-04:00", "UTC"

    // If the raw offset value is "UTC" or "GMT" (without numeric parts), it's 0 offset.
    if (offsetString === 'UTC' || offsetString === 'GMT') {
        return 0;
    }

    // Remove "GMT" prefix if present (e.g., "GMT-5" -> "-5", "GMT+05:30" -> "+05:30")
    offsetString = offsetString.replace(/^GMT\s*/i, '');

    // If after removing "GMT", the string is empty (i.e., original was just "GMT"), it's 0 offset.
    // This is a bit redundant due to the check above but safe.
    if (offsetString === '') {
        return 0;
    }

    // Regex to capture sign, hours, and optional minutes
    // e.g., "-5", "+05:30", "-04:00"
    const matchResult = offsetString.match(/([+-])(\d{1,2})(?::?(\d{2}))?/);

    if (!matchResult) {
        // This could happen if offsetString is 'UTC' (if it somehow slipped through previous checks)
        // or any other format not matching the regex (e.g., 'Z' if Intl ever returns that).
        if (offsetString.toUpperCase() === 'UTC') {
            // Final check for 'UTC' string
            return 0;
        }
        console.error(
            `offsetMinutes: Could not parse offset string '${offsetString}' (from '${timeZoneNamePart.value}') for timezone '${tz}'. Assuming 0 offset.`,
        );
        return 0;
    }

    const [, sign, hoursStr, minutesStr] = matchResult; // minutesStr can be undefined

    // Should not happen if there is a match result
    assert(hoursStr, 'hoursStr is undefined');

    const hours = parseInt(hoursStr, 10);
    const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;

    if (isNaN(hours) || isNaN(minutes)) {
        console.error(
            `offsetMinutes: Parsed NaN from offset string '${offsetString}' for timezone '${tz}'. Assuming 0 offset.`,
        );
        return 0;
    }

    let totalMinutes = hours * 60 + minutes;
    if (sign === '-') {
        totalMinutes = -totalMinutes;
    }

    return totalMinutes;
}

/**
  Returns a UTC ISO-8601 string for the start (or end) of a calendar
  day in a given IANA time-zone.
  --- dateIn accepted forms ---
  Date object - any Date value; only its calendar portion is used.
  Date-only string - "YYYY-MM-DD".
  Full ISO string - "YYYY-MM-DDTHH:mm[:ss][.SSS][Z|±hh:mm]".
  If a time/offset is present, it is ignored; the first 10 chars
  (YYYY-MM-DD) are taken as the local calendar day.
  @param dateIn  Calendar day to convert (formats above).
  @param tz      IANA zone, e.g. "America/Chicago".
  @param isEnd   false → 00:00:00.000 local; true → 23:59:59.999 local.
  @returns       ISO string in UTC (always ends with "Z").
  Example:
  midnightUtcIso('2025-07-02', 'America/Chicago')
  // → "2025-07-02T05:00:00.000Z"     (start of day)
  midnightUtcIso('2025-07-02', 'America/Chicago', true)
  // → "2025-07-03T04:59:59.999Z"     (end of day)
  */
export function midnightUtcIso(
    dateIn: string | Date,
    tz: string,
    { isEnd = false }: { isEnd?: boolean } = {},
): string {
    // --- 1. Normalise to YYYY-MM-DD string ----------------------------------
    const dateStr =
        typeof dateIn === 'string'
            ? dateIn.slice(0, 10) // drop any time / offset
            : dateIn.toISOString().slice(0, 10); // Date → ISO → slice

    const [y, m, d] = dateStr.split('-').map(Number) as [
        number,
        number,
        number,
    ];

    // --- 2. Build the intended wall-clock time in that zone -----------------
    // This creates a UTC timestamp representing the *components* of the local date/time
    const localTimeAsUtcTimestamp = Date.UTC(
        y,
        m - 1, // Month is 0-indexed in Date.UTC
        d,
        isEnd ? 23 : 0,
        isEnd ? 59 : 0,
        isEnd ? 59 : 0,
        isEnd ? 999 : 0,
    );

    // --- 3. Shift by zone offset to get the real UTC instant ----------------
    // Get the offset of the target timezone AT the UTC instant defined by localTimeAsUtcTimestamp.
    const offsetMin = offsetMinutes(new Date(localTimeAsUtcTimestamp), tz);

    // To get the actual UTC instant corresponding to the local wall-clock time:
    // UTC = LocalWallClockTime_Interpreted_As_UTC - OffsetInMinutes
    // Example: If local is 2025-01-01T00:00:00 in America/New_York (EST, UTC-5)
    // localTimeAsUtcTimestamp = timestamp for 2025-01-01T00:00:00Z
    // offsetMin = -300 (for EST)
    // Result = localTimeAsUtcTimestamp - (-300 * 60_000)
    //        = localTimeAsUtcTimestamp + (300 * 60_000)
    //        = 2025-01-01T00:00:00Z + 5 hours = 2025-01-01T05:00:00Z
    return new Date(localTimeAsUtcTimestamp - offsetMin * 60_000).toISOString();
}

/**
  Formats relative time (timezone independent)
  @example "2 hours ago" | "just now" | "3 days ago"
  */
export const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
        return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }

    // Approximation: using 30 days for a month
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12); // Or Math.floor(diffInDays / 365)
    return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
};

/**
  Compact, tense-free duration between `now` and a date (absolute value, so it
  works for past and future). Callers compose "starts in …" / "… ago".
  @example "25 minutes" | "1 hour" | "3 days" | "less than a minute"
  */
export function formatDurationFromNow(dateString: string, now: Date): string {
    const diffInSeconds = Math.abs(
        Math.floor((now.getTime() - new Date(dateString).getTime()) / 1000),
    );

    if (diffInSeconds < 60) {
        return 'less than a minute';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours === 1 ? '' : 's'}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
        return `${diffInDays} day${diffInDays === 1 ? '' : 's'}`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'}`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears === 1 ? '' : 's'}`;
}

/**
 * Formats a date as YYYY-MM-DD string
 * @param date - Date object to format
 * @param locale - Locale to use for formatting
 * @returns Date formatted as YYYY-MM-DD
 * @example
 * formatDateIso(new Date('2025-07-10T15:30:00Z'))
 * // → "2025-07-10"
 */
export function formatDateIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Gets a date as YYYY-MM-DD string in a specific timezone
 * @param date - Date object to format (defaults to current date)
 * @param tz - IANA timezone identifier (e.g., 'America/New_York')
 * @returns Date formatted as YYYY-MM-DD in the specified timezone
 * @example
 * getDateStringInTimezone(new Date(), 'America/New_York')
 * // → "2025-01-15"
 */
export function getDateStringInTimezone(
    date: Date = new Date(),
    tz: string,
): string {
    // Use en-CA locale which gives us YYYY-MM-DD format directly
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    return formatter.format(date);
}

/**
 * Gets the timezone abbreviation (e.g., "EST", "CST", "PDT")
 * @param iso ISO date string
 * @param tz IANA timezone identifier
 * @returns Timezone abbreviation
 */
export function getTimezoneAbbreviation(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dt = new Date(iso);
    const parts = new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        timeZoneName: 'short',
    }).formatToParts(dt);

    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
    return timeZonePart?.value || tz;
}

/**
 * Formats date and time with timezone abbreviation
 * @example "Jan 15, 2025 at 2:30 PM EST"
 */
export function formatDateAndTimeWithTz(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const dateTime = formatDateAndTime(iso, tz, locale);
    const tzAbbr = getTimezoneAbbreviation(iso, tz, locale);
    return `${dateTime} ${tzAbbr}`;
}

/**
 * Formats time with timezone abbreviation
 * @example "2:30 pm EST"
 */
export function formatTimeWithTz(
    iso: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    const time = formatTime(iso, tz, locale);
    const tzAbbr = getTimezoneAbbreviation(iso, tz, locale);
    return `${time} ${tzAbbr}`;
}

/**
 * Converts a wall-clock Date that should be interpreted in `tz`
 * into an ISO string in UTC (ends with "Z").
 *
 * `dateLocal`'s *calendar components* (Y-M-D-h-m-s) are treated
 * as being in `tz`, **regardless of the browser's own zone**.
 *
 * @example
 * // User selects "6 PM" in UI, org is in Chicago
 * const selectedDate = new Date(2025, 5, 23, 18, 0, 0); // 6 PM browser local
 * wallClockUtcIso(selectedDate, 'America/Chicago')
 * // → "2025-06-23T23:00:00.000Z" (6 PM CST = 11 PM UTC in summer)
 */
export function wallClockUtcIso(dateLocal: Date, tz: string): string {
    const y = dateLocal.getFullYear();
    const m = dateLocal.getMonth(); // 0-based
    const d = dateLocal.getDate();
    const h = dateLocal.getHours();
    const mm = dateLocal.getMinutes();
    const ss = dateLocal.getSeconds();
    const ms = dateLocal.getMilliseconds();

    // 1. Build "YYYY-MM-DDThh:mm:ss.sss" as *if it were UTC*.
    const asIfUtc = Date.UTC(y, m, d, h, mm, ss, ms);

    // 2. Shift by the offset of `tz` *at that instant*.
    const offsetMin = offsetMinutes(new Date(asIfUtc), tz);
    const utcEpoch = asIfUtc - offsetMin * 60_000;

    return new Date(utcEpoch).toISOString();
}

/**
 * Returns a Date object representing the start of day (00:00:00.000)
 * in the specified timezone.
 *
 * @param date - The date to get the start of day for. Can be a Date object,
 * or a "YYYY-MM-DD" string.
 * @param tz - IANA timezone identifier (e.g., 'America/New_York')
 * @returns Date object in UTC representing start of day in the given timezone
 *
 * @example
 * // Get start of June 30, 2025 in New York time from a Date object
 * const date = new Date('2025-06-30T15:30:00Z');
 * startOfDayInTimezone(date, 'America/New_York')
 * // → Date object for 2025-06-30T04:00:00.000Z (midnight EDT = 4 AM UTC)
 *
 * @example
 * // Get start of July 10, 2025 in Chicago time from a string
 * startOfDayInTimezone('2025-07-10', 'America/Chicago')
 * // → Date object for 2025-07-10T05:00:00.000Z (midnight CDT = 5 AM UTC)
 */
export function startOfDayInTimezone(date: Date | string, tz: string): Date {
    const isoString = midnightUtcIso(date, tz, { isEnd: false });
    return new Date(isoString);
}

/**
 * Returns a Date object representing the end of day (23:59:59.999)
 * in the specified timezone.
 *
 * @param date - The date to get the end of day for. Can be a Date object,
 * or a "YYYY-MM-DD" string.
 * @param tz - IANA timezone identifier (e.g., 'America/New_York')
 * @returns Date object in UTC representing end of day in the given timezone
 *
 * @example
 * // Get end of June 30, 2025 in New York time from a Date object
 * const date = new Date('2025-06-30T15:30:00Z');
 * endOfDayInTimezone(date, 'America/New_York')
 * // → Date object for 2025-07-01T03:59:59.999Z (11:59:59 PM EDT = 3:59:59 AM UTC next day)
 */
export function endOfDayInTimezone(date: Date | string, tz: string): Date {
    const isoString = midnightUtcIso(date, tz, { isEnd: true });
    return new Date(isoString);
}

/**
 * Converts a Date to a date key (YYYY-MM-DD) in the specified timezone.
 * This is for representing a calendar date in a specific timezone,
 * independent of the browser's local timezone.
 *
 * @param date - The Date object to format
 * @param tz - IANA timezone identifier (e.g., 'America/New_York')
 * @returns Date string as YYYY-MM-DD in the specified timezone
 *
 * @example
 * // Get the date key for a UTC instant in Chicago time
 * toDateKey(new Date('2025-08-13T03:00:00Z'), 'America/Chicago')
 * // → "2025-08-12" (10 PM CDT on Aug 12)
 */
export function toDateKey(date: Date, tz: string): string {
    return formatInTimeZone(date, tz, 'yyyy-MM-dd');
}

/**
 * Converts a date key (YYYY-MM-DD) to the start of that day in UTC.
 *
 * @param dateKey - Date string as YYYY-MM-DD
 * @param tz - IANA timezone identifier
 * @returns ISO string in UTC for the start of that date in the timezone
 *
 * @example
 * dateKeyToStartUtcIso('2025-08-13', 'America/Chicago')
 * // → "2025-08-13T05:00:00.000Z" (midnight CDT = 5 AM UTC)
 */
export function dateKeyToStartUtcIso(dateKey: string, tz: string): string {
    return fromZonedTime(`${dateKey}T00:00:00`, tz).toISOString();
}

/**
 * Converts a date key (YYYY-MM-DD) to the end of that day in UTC.
 * Uses 23:59:59.999 instead of 24:00:00 for the end of day.
 *
 * @param dateKey - Date string as YYYY-MM-DD
 * @param tz - IANA timezone identifier
 * @returns ISO string in UTC for the end of that date in the timezone
 *
 * @example
 * dateKeyToEndUtcIso('2025-08-13', 'America/Chicago')
 * // → "2025-08-14T04:59:59.999Z" (11:59:59 PM CDT = 4:59:59 AM UTC next day)
 */
export function dateKeyToEndUtcIso(dateKey: string, tz: string): string {
    return fromZonedTime(`${dateKey}T23:59:59.999`, tz).toISOString();
}

/**
 * Converts a timezone-less ISO datetime to a UTC ISO string.
 *
 * @param localIso - Datetime string without timezone (e.g. "2026-05-13T18:00")
 * @param tz - IANA timezone the datetime is expressed in
 * @returns UTC ISO string
 *
 * @example
 * zonedTimeToUtcIso('2026-05-13T18:00', 'America/New_York')
 * // → "2026-05-13T22:00:00.000Z"
 */
export function zonedTimeToUtcIso(localIso: string, tz: string): string {
    return fromZonedTime(localIso, tz).toISOString();
}

/**
 * Creates a safe Date object for calendar display that represents
 * the midday of a date key in the organization's timezone.
 * Using midday avoids DST edge cases and ensures correct calendar square selection.
 *
 * @param dateKey - Date string as YYYY-MM-DD
 * @param tz - IANA timezone identifier
 * @returns Date object representing midday of that date
 *
 * @example
 * dateKeyToCalendarDate('2025-08-13', 'America/Chicago')
 * // → Date object for 2025-08-13T17:00:00.000Z (noon CDT = 5 PM UTC)
 */
export function dateKeyToCalendarDate(dateKey: string, tz: string): Date {
    // Use midday to avoid DST boundaries
    return fromZonedTime(`${dateKey}T12:00:00`, tz);
}

/**
 * Formats a date key for display with full date formatting.
 *
 * @param dateKey - Date string as YYYY-MM-DD
 * @param tz - IANA timezone identifier
 * @param locale - Locale for formatting (optional)
 * @returns Formatted date string
 *
 * @example
 * formatDateKey('2025-08-13', 'America/Chicago')
 * // → "Aug 13, 2025"
 */
export function formatDateKey(
    dateKey: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    // Parse the date key as a date in the given timezone at noon (to avoid DST issues)
    const date = fromZonedTime(`${dateKey}T12:00:00`, tz);
    return formatFullDate(date, tz, locale);
}

/**
 * Formats a date key for display with weekday and year.
 *
 * @param dateKey - Date string as YYYY-MM-DD
 * @param tz - IANA timezone identifier
 * @param locale - Locale for formatting (optional)
 * @returns Formatted date string with weekday
 *
 * @example
 * formatDateKeyLong('2025-08-13', 'America/Chicago')
 * // → "Wed, Aug 13, 2025"
 */
export function formatDateKeyLong(
    dateKey: string,
    tz: string,
    locale = DEFAULT_LOCALE,
): string {
    // Parse the date key as a date in the given timezone at noon (to avoid DST issues)
    const date = fromZonedTime(`${dateKey}T12:00:00`, tz);
    return formatLongDate(date, tz, locale);
}

/** Formats the month and year for a YYYY-MM-DD date key. */
export function formatMonthKey(dateKey: string, tz: string): string {
    const date = fromZonedTime(`${dateKey}T12:00:00`, tz);
    return formatInTimeZone(date, tz, 'LLLL yyyy');
}

/**
 * Formats an inclusive YYYY-MM-DD date-key range for compact display.
 * With `currentYear`, dates in that year leave the year out.
 */
export function formatDateKeyRange(
    startKey: string,
    endKey: string,
    tz: string,
    options: Readonly<{ currentYear?: string }> = {},
): string {
    const start = fromZonedTime(`${startKey}T12:00:00`, tz);
    const end = fromZonedTime(`${endKey}T12:00:00`, tz);

    const startMonth = formatInTimeZone(start, tz, 'MMM');
    const startDay = formatInTimeZone(start, tz, 'd');
    const startYear = formatInTimeZone(start, tz, 'yyyy');
    const endMonth = formatInTimeZone(end, tz, 'MMM');
    const endDay = formatInTimeZone(end, tz, 'd');
    const endYear = formatInTimeZone(end, tz, 'yyyy');
    const year = (value: string) =>
        value === options.currentYear ? '' : `, ${value}`;

    if (startKey === endKey) {
        return `${startMonth} ${startDay}${year(startYear)}`;
    }
    if (startYear !== endYear) {
        return `${startMonth} ${startDay}${year(startYear)} – ${endMonth} ${endDay}${year(endYear)}`;
    }
    if (startMonth !== endMonth) {
        return `${startMonth} ${startDay} – ${endMonth} ${endDay}${year(endYear)}`;
    }
    return `${startMonth} ${startDay} – ${endDay}${year(endYear)}`;
}

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

function parseDateKeyToUtc(dateKey: string): Date {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, d ?? 1));
}

function formatUtcDateKey(dt: Date): string {
    const yyyy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

/** True only for a real `YYYY-MM-DD` date, so `2026-02-30` is rejected. */
export function isValidDateKey(value: string | undefined): value is string {
    return (
        !!value &&
        DATE_KEY_PATTERN.test(value) &&
        formatUtcDateKey(parseDateKeyToUtc(value)) === value
    );
}

/** Adds `days` to a YYYY-MM-DD dateKey using UTC so it never shifts across timezones/DST. */
export function shiftDateKey(dateKey: string, days: number): string {
    const dt = parseDateKeyToUtc(dateKey);
    dt.setUTCDate(dt.getUTCDate() + days);
    return formatUtcDateKey(dt);
}

/** Returns the Sunday date key for the week containing `dateKey`. */
export function startOfWeekDateKey(dateKey: string): string {
    const dt = parseDateKeyToUtc(dateKey);
    dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
    return formatUtcDateKey(dt);
}

/** Returns the Monday date key for the ISO-style week containing `dateKey`. */
export function mondayStartDateKey(dateKey: string): string {
    const dt = parseDateKeyToUtc(dateKey);
    const daysSinceMonday = (dt.getUTCDay() + 6) % 7;
    dt.setUTCDate(dt.getUTCDate() - daysSinceMonday);
    return formatUtcDateKey(dt);
}

/** Returns the Saturday date key for the week containing `dateKey`. */
export function endOfWeekDateKey(dateKey: string): string {
    const dt = parseDateKeyToUtc(dateKey);
    dt.setUTCDate(dt.getUTCDate() + (6 - dt.getUTCDay()));
    return formatUtcDateKey(dt);
}

/** Returns the first date key in the month containing `dateKey`. */
export function startOfMonthDateKey(dateKey: string): string {
    const dt = parseDateKeyToUtc(dateKey);
    dt.setUTCDate(1);
    return formatUtcDateKey(dt);
}

/** Returns the last date key in the month containing `dateKey`. */
export function endOfMonthDateKey(dateKey: string): string {
    const dt = parseDateKeyToUtc(dateKey);
    dt.setUTCMonth(dt.getUTCMonth() + 1, 0);
    return formatUtcDateKey(dt);
}

/** Adds months to a date key, clamping to the target month's last valid day. */
export function addMonthsToDateKey(dateKey: string, months: number): string {
    const dt = parseDateKeyToUtc(dateKey);
    const targetDay = dt.getUTCDate();
    dt.setUTCDate(1);
    dt.setUTCMonth(dt.getUTCMonth() + months);
    const lastDayOfTargetMonth = new Date(
        Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 0),
    ).getUTCDate();
    dt.setUTCDate(Math.min(targetDay, lastDayOfTargetMonth));
    return formatUtcDateKey(dt);
}

/** Build a local Date at noon that always highlights the intended calendar cell. */
export function localCalendarDateFromKey(dateKey: string): Date {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(y ?? 2000, (m ?? 1) - 1, d ?? 0, 12, 0, 0, 0); // noon local time
}

/** Read YYYY-MM-DD from a Date coming from the calendar (which uses the browser timezone). */
export function dateKeyFromLocalCalendarDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Instant (ISO) at local midnight starting the given calendar day. */
export function localDayStartIso(dateKey: string): string {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(y ?? 2000, (m ?? 1) - 1, d ?? 1).toISOString();
}

/** Instant (ISO) at local midnight ending the given calendar day (exclusive). */
export function localDayEndIso(dateKey: string): string {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(y ?? 2000, (m ?? 1) - 1, (d ?? 1) + 1).toISOString();
}
